import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../services/hooks';
import { useActions } from '../../services/hooks/useActions';
import { linkedClientsSelector, userSelector, activeContactSelector } from '../../services/selectors/user.selectors';
import { useSwitchPatientMutation } from '../../services/api/user.api';
import { tWithKeyAttr as t } from '../../i18nHelper';
import BackArrowButton from '../../components/backArrowButton/backArrowButton';
import styles from './selectPatient.module.css';
import { TLinkedClient } from '../../services/types/auth.types';

interface IPatient {
  clientId: string;
  fullName: string; 
  relation?: string;
  birth?: string;
  [key: string]: any;
}

export const SelectPatientPage = () => {
  const navigate = useNavigate();
  const linkedClients = useAppSelector(linkedClientsSelector);
  const currentUser = useAppSelector(userSelector);
  const activeContact = useAppSelector(activeContactSelector);
  const { switchPatient: switchPatientAction } = useActions();
  const [switchPatient, { isLoading }] = useSwitchPatientMutation();

  const handleSelectPatient = async (patientId: string) => {
    try {
      const result = await switchPatient({ patientId }).unwrap();
      if (result.success) {
        switchPatientAction(result);
        navigate('/home');
      }
    } catch (error) {
      console.error('Failed to switch patient:', error);
    }
  };

  const contactName = activeContact?.firstName || activeContact?.name || '';

  return (
    <div className={styles.container}> 
      <div className={styles.backArrowWrapper}>
        <BackArrowButton position={"center"} onClick={() => navigate(-1)} />
      </div>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('SELECT_PATIENT')}</h1>
        {contactName && (
          <p className={styles.welcomeMessage}>
            {t('WELCOME_CONTACT_MESSAGE', { contactName })}
          </p>
        )}
      </div>
      <div className={styles.patientList}>
        {linkedClients?.map((client: TLinkedClient) => {
          const age = client.birth ? calculateAge(client.birth) : null;
          const isSelected = client.clientId.toString() === currentUser.id.toString();
          
          return (
            <button
              key={client.clientId}
              className={`${styles.patientCard} ${isSelected ? styles.selected : ''}`}
              onClick={() => handleSelectPatient(`${client.clientId}`)}
              disabled={isLoading || isSelected}
            >
              <div className={styles.patientInfo}>
                <div className={styles.patientHeader}>
                  <h3 className={styles.patientName}>{client.fullName}</h3>
                  {isSelected && <span className={styles.checkmark}>✓</span>}
                </div>
                <div className={styles.patientDetails}>
                  {client.relation && <span className={styles.relation}>{client.relation}</span>}
                  {age && <span className={styles.age}>{t('AGE')}: {age}</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

function calculateAge(birthDate: string): number {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}
